import React from 'react';
import { Box, Button, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import SearchableSelect, { SelectOption } from '../../common/SearchableSelect';

export interface DraftLine {
  key: number;
  accountId: string;
  description: string;
  debit: string;
  credit: string;
}

let nextKey = 1;
export const blankLine = (): DraftLine => ({ key: nextKey++, accountId: '', description: '', debit: '', credit: '' });

const AMOUNT = /^\d*(\.\d{0,2})?$/;

/**
 * Debit/credit lines being entered. Typing on one side clears the other,
 * so every line stays one-sided without a rule to explain.
 */
const JournalLinesEditor: React.FC<{
  lines: DraftLine[];
  onChange: (lines: DraftLine[]) => void;
  accountOptions: SelectOption[];
  currency: string;
  disabled?: boolean;
}> = ({ lines, onChange, accountOptions, currency, disabled }) => {
  const patch = (key: number, change: Partial<DraftLine>) =>
    onChange(lines.map(l => (l.key === key ? { ...l, ...change } : l)));

  const amountField = (line: DraftLine, side: 'debit' | 'credit') => (
    <TextField
      size="small"
      placeholder="0.00"
      value={line[side]}
      disabled={disabled}
      onChange={e => {
        const v = e.target.value.replace(/,/g, '');
        if (!AMOUNT.test(v)) return;
        patch(line.key, side === 'debit' ? { debit: v, credit: v ? '' : line.credit } : { credit: v, debit: v ? '' : line.debit });
      }}
      inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }, 'aria-label': side }}
      InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" color="text.disabled">{currency}</Typography></InputAdornment> }}
      sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0 }}
    />
  );

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ display: { xs: 'none', sm: 'flex' }, px: 0.5, pb: 0.75 }}>
        {['Account', 'Note'].map((h, i) => (
          <Typography key={h} variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: i === 0 ? 1.4 : 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>{h}</Typography>
        ))}
        {['Debit', 'Credit'].map(h => (
          <Typography key={h} variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 150, textAlign: 'right', textTransform: 'uppercase', letterSpacing: 0.6 }}>{h}</Typography>
        ))}
        <Box sx={{ width: 34 }} />
      </Stack>

      <Stack spacing={1.25}>
        {lines.map(line => (
          <Stack key={line.key} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1.4, minWidth: 0 }}>
              <SearchableSelect
                value={line.accountId}
                onChange={v => patch(line.key, { accountId: v })}
                options={accountOptions}
                placeholder="Choose account"
                fullWidth
                minWidth={0}
                disabled={disabled}
              />
            </Box>
            <TextField
              size="small"
              placeholder="Optional"
              value={line.description}
              disabled={disabled}
              onChange={e => patch(line.key, { description: e.target.value })}
              inputProps={{ maxLength: 300 }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            {amountField(line, 'debit')}
            {amountField(line, 'credit')}
            <Tooltip title="Remove line">
              <span>
                <IconButton size="small" onClick={() => onChange(lines.filter(l => l.key !== line.key))} disabled={disabled || lines.length <= 2}>
                  <Close fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      <Button startIcon={<Add />} size="small" onClick={() => onChange([...lines, blankLine()])} disabled={disabled} sx={{ mt: 1.5 }}>
        Add line
      </Button>
    </Box>
  );
};

export default JournalLinesEditor;
